import React from 'react';
import { useTranslation } from 'react-i18next';

interface WizardHeaderProps {
  onBack: () => void;
  onClose: () => void;
  // 0-based index of the active step group (0 = address/building, 1 = size,
  // 2 = rooms). When null, the dot progress indicator is hidden (publish /
  // login screens).
  activeDot: number | null;
  // When the active step's input is already satisfied (valid size / selected
  // rooms), render its dot as fully filled ("done") instead of the active ring.
  activeDotDone?: boolean;
  totalDots?: number;
  isShareStep?: boolean;
  // Hide the back button on screens that are not reached from a previous step
  // (e.g. the could-buy main screen, which Figma shows with no back arrow).
  hideBack?: boolean;
  // Overrides the centered header title (defaults to the "Add my place" wizard
  // title); CouldBuyFlow, the edit modal and the estimate flow pass their own.
  title?: string;
  // Optional leading glyph rendered inline before the title (Figma shows a euro
  // coin beside the could-buy header). Omitted by default so existing callers
  // are visually unchanged.
  titleIcon?: React.ReactNode;
  // When provided, the stepper dots become clickable and call this with the
  // dot's index, so the user can jump straight to that step (edit modal).
  onDotClick?: (index: number) => void;
}

const WizardHeader = ({
  onBack,
  onClose,
  activeDot,
  activeDotDone = false,
  totalDots = 3,
  isShareStep = false,
  hideBack = false,
  title,
  titleIcon,
  onDotClick,
}: WizardHeaderProps) => {
  const { t } = useTranslation();

  return (
    <header className="amp-flow__header">
      <div className="amp-flow__header-inner">
        <button
          type="button"
          className={`amp-flow__header-button${isShareStep || hideBack ? ' amp-flow__header-button--hidden' : ''}`}
          onClick={onBack}
          aria-label={t('newLandingPage.addMyPlace.backAria')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M9.81283 3.01849C10.1708 2.6605 10.7511 2.66051 11.1091 3.01849C11.4668 3.37648 11.467 3.9568 11.1091 4.31471L5.3405 10.0833H19.6294C20.1357 10.0833 20.5461 10.4937 20.5461 10.9999C20.5459 11.506 20.1355 11.9166 19.6294 11.9166H5.3405L11.1091 17.6852C11.4668 18.0432 11.467 18.6235 11.1091 18.9814C10.7511 19.3392 10.1708 19.3391 9.81283 18.9814L2.80355 11.9721C2.26664 11.4351 2.26667 10.5647 2.80355 10.0278L9.81283 3.01849Z" fill="#29363D" />
          </svg>
        </button>

        <div className="amp-flow__header-center">
          <span className="amp-flow__header-title-row">
            {titleIcon ? (
              <span className="amp-flow__header-title-icon" aria-hidden="true">
                {titleIcon}
              </span>
            ) : null}
            <span className="amp-flow__header-title">{title ?? t('newLandingPage.addMyPlace.title')}</span>
          </span>
        </div>

        <button
          type="button"
          className="amp-flow__header-button"
          onClick={onClose}
          aria-label={t('newLandingPage.addMyPlace.closeAria')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M16.6064 3.77311C17.0539 3.32563 17.7792 3.32563 18.2267 3.77311C18.6741 4.22059 18.6742 4.94594 18.2267 5.39339L12.6202 10.9999L18.2267 16.6064C18.6741 17.0539 18.6742 17.7793 18.2267 18.2267C17.7793 18.6742 17.0539 18.6741 16.6064 18.2267L10.9999 12.6202L5.39339 18.2267C4.94594 18.6742 4.22059 18.6741 3.77311 18.2267C3.32563 17.7792 3.32563 17.0539 3.77311 16.6064L9.37963 10.9999L3.77311 5.39339C3.32563 4.94591 3.32563 4.22058 3.77311 3.77311C4.22058 3.32563 4.94591 3.32563 5.39339 3.77311L10.9999 9.37963L16.6064 3.77311Z" fill="#29363D" />
          </svg>
        </button>
      </div>
      {activeDot !== null && (
        <div className="amp-flow__dots" role={onDotClick ? undefined : 'presentation'}>
          {Array.from({ length: totalDots }).map((_, index) => {
            const state = index < activeDot
              ? 'done'
              : index === activeDot
                ? (activeDotDone ? 'done' : 'active')
                : 'upcoming';
            const className = `amp-flow__dot amp-flow__dot--${state}`;
            return onDotClick ? (
              // eslint-disable-next-line react/no-array-index-key
              <button
                key={index}
                type="button"
                className={className}
                onClick={() => onDotClick(index)}
                aria-label={`Step ${index + 1}`}
              />
            ) : (
              // eslint-disable-next-line react/no-array-index-key
              <span key={index} className={className} />
            );
          })}
        </div>
      )}
    </header>
  );
};

export default WizardHeader;
